<script setup lang="ts">
/**
 * Datos del negocio, con el mismo layout que Profile: ficha a la izquierda,
 * pestañas a la derecha (`pages/apps/user/view/[id].vue` de la plantilla).
 *
 * La zona de peligro vive acá, en una pestaña, y ya no como item del menú.
 * Borrar la cuenta es lo último que se le hace a una empresa, no una sección
 * que se visite: tenerla al mismo nivel que "Preferences" le daba una presencia
 * permanente que no se gana.
 */
import { useRoute, useRouter } from 'vue-router'
import { deleteAccount, deleteLogo, updateBusiness, uploadLogo } from '@/api/account'
import { useAuthStore } from '@/stores/auth'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import ProviderActivityTimeline, { type TimelineEntry } from '@/views/settings/ProviderActivityTimeline.vue'
import ProviderCompanyPanel from '@/views/settings/ProviderCompanyPanel.vue'
import { listDiveCenters } from '@/api/dive-centers'
import { apiMessage, notifyError, notifySuccess } from '@/composables/useSnackbar'
import { countries, countryName } from '@/data/countries'

definePage({ meta: { requiresAuth: true } })

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const confirmRef = ref<InstanceType<typeof ConfirmDialog> | null>(null)

const tabs = [
  { icon: 'ri-building-line', title: 'Company' },
  { icon: 'ri-error-warning-line', title: 'Danger zone' },
]

const currentTab = ref(0)

/*
 * La derecha arranca en **lectura**, igual que en Profile: entrar a Company es
 * mirar los datos del negocio, no cambiarlos.
 */
const editing = ref(false)

const timeline = ref<TimelineEntry[]>([])

const DEFAULT_LOGO = '/images/avatar-default.png'

const logoInput = ref<HTMLInputElement>()
const logoBusy = ref(false)

const displayName = ref('')
const taxId = ref('')
const phone = ref('')
const country = ref('')
const website = ref('')
const saving = ref(false)

// Danger zone
const confirmText = ref('')
const deleting = ref(false)

const provider = computed(() => auth.provider)
const isOwner = computed(() => auth.providerUser?.role === 'owner')
const canEdit = computed(
  () => auth.providerUser?.role === 'owner' || auth.providerUser?.role === 'admin',
)

const isCompanyAccount = computed(() => provider.value?.accountType === 'company')

function resetForm() {
  const p = provider.value
  if (!p)
    return

  displayName.value = p.displayName
  taxId.value = p.taxId ?? ''
  phone.value = p.phone ?? ''

  // Back-compat: los registros viejos guardaban el código alpha-2 ('ES'); el
  // dropdown está indexado por nombre completo, así que se promueve al cargar.
  const raw = p.country ?? ''

  country.value = raw.length === 2 ? (countryName(raw) ?? raw) : raw
  website.value = p.website ?? ''
}

onMounted(async () => {
  await auth.refreshMe()
  resetForm()
  buildTimeline()

  /*
   * `/settings/danger` redirige acá con `?tab=danger`. Sin esto, un enlace
   * guardado de la versión anterior caía en la ficha sin decir a qué venía, y
   * además hay que abrir la edición: la zona de peligro vive ahí adentro.
   */
  if (route.query.tab === 'danger') {
    currentTab.value = 1
    editing.value = true
  }
})

/** Hechos reales de la cuenta. Mismo criterio y mismo límite que en Profile. */
async function buildTimeline() {
  const entries: TimelineEntry[] = []
  const p = provider.value

  if (p?.verifiedAt)
    entries.push({ title: 'Account verified', text: 'Blue Trails approved this account.', at: p.verifiedAt, color: 'success' })

  if (p?.createdAt)
    entries.push({ title: 'Account created', text: p.displayName, at: p.createdAt, color: 'secondary' })

  try {
    const res = await listDiveCenters()

    for (const dc of res.diveCenters) {
      if (dc.createdAt)
        entries.push({ title: `Dive center added: ${dc.name}`, text: dc.address?.city || undefined, at: dc.createdAt, color: 'warning' })
    }
  }
  catch { /* la línea de tiempo es contexto: sin los centros igual sirve */ }

  timeline.value = entries
}

function cancelEdit() {
  editing.value = false
  resetForm()
  if (route.query.tab)
    router.replace({ query: {} })
}

/** Mismo flujo que el avatar del perfil: la URL vuelve firmada del backend. */
async function changeLogo(e: Event) {
  const file = (e.target as HTMLInputElement).files?.[0]
  if (!file)
    return

  logoBusy.value = true
  try {
    await uploadLogo(file)
    await auth.refreshMe()
    notifySuccess('Logo updated.')
  }
  catch (err) {
    notifyError(apiMessage(err, 'Could not upload the logo.'))
  }
  finally {
    logoBusy.value = false
    // Se limpia el input o volver a elegir el MISMO archivo no dispara `change`.
    if (logoInput.value)
      logoInput.value.value = ''
  }
}

async function resetLogo() {
  logoBusy.value = true
  try {
    await deleteLogo()
    await auth.refreshMe()
    notifySuccess('Logo removed.')
  }
  catch (err) {
    notifyError(apiMessage(err, 'Could not remove the logo.'))
  }
  finally {
    logoBusy.value = false
  }
}

async function save() {
  saving.value = true
  try {
    await updateBusiness({
      displayName: displayName.value.trim(),
      taxId: taxId.value.trim() || undefined,
      phone: phone.value.trim() || undefined,
      country: country.value.trim() || undefined,
      website: website.value.trim() || undefined,
    })
    await auth.refreshMe()
    notifySuccess('Company details saved.')
  }
  catch (e) {
    notifyError(apiMessage(e, 'Could not save.'))
  }
  finally {
    saving.value = false
  }
}

// Doble freno: hay que escribir la frase Y confirmar el diálogo. Borra el
// negocio entero, así que un click de más no debería alcanzar.
async function confirmDelete() {
  if (confirmText.value.trim().toLowerCase() !== 'delete my account') {
    notifyError("Type 'delete my account' to confirm.")

    return
  }

  const ok = await confirmRef.value?.open({
    title: 'Delete account',
    message: 'This permanently deletes your business, dive centers and photos. Continue?',
    confirmText: 'Delete account',
    confirmColor: 'error',
  })
  if (!ok)
    return

  deleting.value = true
  try {
    await deleteAccount()
    auth.logout()
    router.push({ name: 'login' })
  }
  catch (e) {
    notifyError(apiMessage(e, 'Could not delete account.'))
  }
  finally {
    deleting.value = false
  }
}
</script>

<template>
  <div>
    <!-- Blue Trails verifica la cuenta; recién ahí los dive centers publicados
         se vuelven visibles en la app. -->
    <VAlert
      v-if="provider && provider.verificationStatus !== 'verified'"
      :type="provider.verificationStatus === 'rejected' ? 'error' : 'info'"
      variant="tonal"
      density="comfortable"
      class="mb-6"
    >
      <template v-if="provider.verificationStatus === 'rejected'">
        Your account verification was rejected.
        <span v-if="provider.verificationNotes"> Reason: {{ provider.verificationNotes }}</span>
      </template>
      <template v-else-if="provider.verificationStatus === 'pending'">
        Your account is under review by Blue Trails. Published dive centers go live once verified.
      </template>
      <template v-else>
        Your account is waiting for approval. Blue Trails reviews new accounts before their published
        dive centers become visible in the app.
      </template>
    </VAlert>

    <VRow>
      <VCol
        cols="12"
        md="5"
        lg="4"
      >
        <ProviderCompanyPanel
          :provider="provider ?? null"
          :can-edit="canEdit"
          :is-owner="isOwner"
          @edit="editing = true; currentTab = 0"
          @danger="editing = true; currentTab = 1"
        />
      </VCol>

      <VCol
        cols="12"
        md="7"
        lg="8"
      >
        <!-- Lectura: qué pasó en la cuenta. -->
        <ProviderActivityTimeline
          v-if="!editing"
          :entries="timeline"
        />

        <VTabs
          v-else
          v-model="currentTab"
          class="v-tabs-pill"
        >
          <VTab
            v-for="tab in tabs"
            :key="tab.icon"
          >
            <VIcon
              start
              :icon="tab.icon"
            />
            <span>{{ tab.title }}</span>
          </VTab>
        </VTabs>

        <VWindow
          v-if="editing"
          v-model="currentTab"
          class="mt-6 disable-tab-transition"
          :touch="false"
        >
          <!-- 👉 Company -->
          <VWindowItem>
            <VCard title="Business details">
              <VCardText>
                <!-- 👉 Logo (markup de `AccountSettingsAccount.vue`) -->
                <div class="d-flex mb-10">
                  <VAvatar
                    rounded
                    size="100"
                    class="me-6"
                    :image="provider?.logoUrl || DEFAULT_LOGO"
                  />

                  <form class="d-flex flex-column justify-center gap-4">
                    <div class="d-flex flex-wrap gap-4">
                      <VBtn
                        color="primary"
                        :loading="logoBusy"
                        :disabled="!canEdit"
                        @click="logoInput?.click()"
                      >
                        <VIcon
                          icon="ri-upload-cloud-line"
                          class="d-sm-none"
                        />
                        <span class="d-none d-sm-block">Upload new logo</span>
                      </VBtn>
                      <input
                        ref="logoInput"
                        type="file"
                        name="file"
                        accept=".jpeg,.png,.jpg,.webp,.gif"
                        hidden
                        @change="changeLogo"
                      >
                      <VBtn
                        type="reset"
                        color="error"
                        variant="outlined"
                        :disabled="!provider?.logoUrl || logoBusy || !canEdit"
                        @click.prevent="resetLogo"
                      >
                        <span class="d-none d-sm-block">Reset</span>
                        <VIcon
                          icon="ri-refresh-line"
                          class="d-sm-none"
                        />
                      </VBtn>
                    </div>
                    <p class="text-body-1 mb-0">
                      Allowed JPG, GIF or PNG. Max size of 800K
                    </p>
                  </form>
                </div>

                <VForm @submit.prevent="save">
                  <VRow>
                    <VCol cols="12">
                      <VTextField
                        v-model="displayName"
                        label="Display name"
                        placeholder="Blue Water Dive"
                        :disabled="saving || !canEdit"
                      />
                    </VCol>

                    <VCol
                      v-if="isCompanyAccount"
                      cols="12"
                      md="6"
                    >
                      <VTextField
                        v-model="taxId"
                        label="Tax ID / VAT"
                        :disabled="saving || !canEdit"
                      />
                    </VCol>

                    <VCol
                      cols="12"
                      md="6"
                    >
                      <VTextField
                        v-model="phone"
                        label="Phone Number"
                        placeholder="+34 600 000 000"
                        :disabled="saving || !canEdit"
                      />
                    </VCol>

                    <VCol
                      cols="12"
                      md="6"
                    >
                      <VAutocomplete
                        v-model="country"
                        :items="countries"
                        item-title="name"
                        item-value="name"
                        label="Country"
                        autocomplete="off"
                        clearable
                        :disabled="saving || !canEdit"
                      />
                    </VCol>

                    <VCol
                      cols="12"
                      md="6"
                    >
                      <VTextField
                        v-model="website"
                        label="Website"
                        type="url"
                        placeholder="https://bluewaterdive.com"
                        :disabled="saving || !canEdit"
                      />
                    </VCol>

                    <VCol cols="12">
                      <p class="text-body-2 text-medium-emphasis mb-0">
                        Currency, date and unit formats live in
                        <RouterLink :to="{ name: 'settings-preferences' }">
                          Preferences
                        </RouterLink>.
                      </p>
                    </VCol>

                    <!-- 👉 Form Actions -->
                    <VCol
                      cols="12"
                      class="d-flex flex-wrap gap-4"
                    >
                      <VBtn
                        type="submit"
                        :loading="saving"
                        :disabled="!canEdit"
                      >
                        Save changes
                      </VBtn>

                      <VBtn
                        color="secondary"
                        variant="outlined"
                        type="reset"
                        :disabled="saving || !canEdit"
                        @click.prevent="cancelEdit"
                      >
                        Cancel
                      </VBtn>
                    </VCol>
                  </VRow>
                </VForm>
              </VCardText>
            </VCard>
          </VWindowItem>

          <!-- 👉 Danger zone -->
          <VWindowItem>
            <VCard title="Delete Account">
              <VCardText>
                <!-- Solo el owner puede borrar la cuenta; el resto ve por qué no puede. -->
                <VAlert
                  v-if="!isOwner"
                  type="info"
                  variant="tonal"
                  density="comfortable"
                >
                  Only the account owner can delete this account.
                </VAlert>

                <template v-else>
                  <VAlert
                    type="warning"
                    variant="tonal"
                    density="comfortable"
                    class="mb-6"
                  >
                    <VAlertTitle class="mb-1">
                      Are you sure you want to delete your account?
                    </VAlertTitle>
                    <span>
                      This permanently removes your business, every dive center, every photo
                      and every login. It cannot be undone.
                    </span>
                  </VAlert>

                  <VTextField
                    v-model="confirmText"
                    label="Type &quot;delete my account&quot; to confirm"
                    placeholder="delete my account"
                    :disabled="deleting"
                  />

                  <VBtn
                    color="error"
                    class="mt-6"
                    :loading="deleting"
                    @click="confirmDelete"
                  >
                    Delete account
                  </VBtn>
                </template>
              </VCardText>
            </VCard>
          </VWindowItem>
        </VWindow>
      </VCol>
    </VRow>

    <ConfirmDialog ref="confirmRef" />
  </div>
</template>
